import {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector, useStore} from 'react-redux';
import {fetchGuestlist} from '~/ViewInvitation/reducers/apiActions';
import {selectEventHasPassed} from '~/ViewInvitation/reducers/eventDetails';
import {isCanceled, isPast} from '~/common/utils/event_utils';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {ArrowIcon} from '~library/svg/ArrowIcon';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {WhoIsComingHeader} from './WhoIsComingHeader';
import {Guestlist} from '~/ViewInvitation/routes/Guests/Guestlist';
import {selectHasRsvpd} from '~/CreateFabricInvitation/reducers/rsvp/selectors';
import {largishAndUp} from '~libSass/base/_exports.sass';
import {selectEventType} from '~/common/fabric/reducers/eviteEvent/selectors';

export const WhoIsComingOverlay = (props) => {
  const {rsvpDetails, open, onClose, preview} = props;
  const store = useStore();
  const [query, setQuery] = useState('');
  const [openCommentEditor, setOpenCommentEditor] = useState();

  const [loading, setLoading] = useState(true);

  const hasRsvpd = useSelector(selectHasRsvpd);
  const eventType = useSelector(selectEventType);
  const isHost = useSelector((state) => state.user?.isHost);
  const isPremium = useSelector((state) => state.eventDetails?.isPremium);
  const showGifting = useSelector((state) => state.eviteEvent?.options?.event_option_show_gifting);
  const origin = useSelector((state) => state.eventDetails?.origin);
  const eventStatus = useSelector((state) => state.eventDetails?.status);
  const yesRsvp = useSelector((state) => state.guestList?.rsvpCounts?.yes);
  const maybeRsvp = useSelector((state) => state.guestList?.rsvpCounts?.maybe);
  const noRsvp = useSelector((state) => state.guestList?.rsvpCounts?.no);
  const undecided = useSelector((state) => state.guestList?.rsvpCounts?.no_reply);
  const {matches: largeScreens} = useMatchQuery(`(min-width: ${largishAndUp})`);

  const isFuneral = eventType === 'funeral_memorial_service';
  const includeMaybe = !isPremium && origin !== 21;
  const replies = !includeMaybe ? ['yes', 'no', 'no_reply'] : ['yes', 'maybe', 'no', 'no_reply'];

  useEffect(() => {
    store.dispatch(
      fetchGuestlist(
        window.event_id,
        () => {
          setLoading(false);
        },
        (dispatch, getState, d) => {
          toastAlert(d?.results?.message || 'Error loading guestlist', {
            type: 'error',
          });
          setLoading(false);
        }
      )
    );
  }, [store]);

  useEffect(() => {
    if (open) {
      document.body.classList.add('no-scroll');
    } else {
      setOpenCommentEditor(undefined);
      document.body.classList.remove('no-scroll');
    }
  }, [open]);

  const allowInviteMore = window.evite_event?.options.event_option_allow_invite_more || false;
  const eventHasPassed = useSelector(selectEventHasPassed) || isPast(eventStatus);
  const eventIsCanceled = isCanceled(eventStatus);
  const showInviteMore =
    (allowInviteMore || isHost) && hasRsvpd && !eventHasPassed && !eventIsCanceled;

  return (
    <>
      <div
        className={`view-invitation__overlay-bg ${open ? 'open' : ''}`}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClose();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="overlay background - click to close"
      />
      <div className={`view-invitation__who-is-coming-overlay ${open ? 'open' : ''}`}>
        <div className="wic-overlay__heading">
          {largeScreens ? (
            <>
              <Typography variant="header5" data-qa-id="guestlist-drawer-header">
                Guestlist
              </Typography>
              <button
                data-qa-id="who-is-coming-close"
                className="unstyled-button"
                onClick={onClose}
                data-impression-tracking="true"
                aria-label="Close overlay"
                type="button"
              >
                <CloseLineIcon />
              </button>
            </>
          ) : (
            <>
              <button
                data-qa-id="who-is-coming-close"
                className="unstyled-button wic-overlay__close-overlay-btn"
                onClick={onClose}
                data-impression-tracking="true"
                type="button"
                aria-label="Close overlay"
              >
                <ArrowIcon />
              </button>
              <Typography variant="header2">Guestlist</Typography>
            </>
          )}
        </div>
        {loading ? (
          <div className="wic-overlay__loading">
            <span className="evt-spinner" />
            <Typography variant="paragraph2">Loading guestlist...</Typography>
          </div>
        ) : (
          <>
            <WhoIsComingHeader
              yes={yesRsvp}
              maybe={includeMaybe ? maybeRsvp : undefined}
              no={noRsvp}
              no_reply={undecided}
              query={query}
              onQueryChange={setQuery}
              allowAddGuests={showInviteMore}
              addGuestsUrl={window.invite_more_url}
              preview={preview}
            />
            <Guestlist
              replies={replies}
              query={query}
              openCommentEditor={openCommentEditor}
              setOpenCommentEditor={setOpenCommentEditor}
              rsvpDetails={rsvpDetails}
              removeAds={isFuneral || !showGifting}
            />
          </>
        )}
      </div>
    </>
  );
};

WhoIsComingOverlay.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  rsvpDetails: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(
      PropTypes.shape({
        avatar_url: PropTypes.string,
        user_id: PropTypes.string,
        guest_id: PropTypes.string.isRequired,
        adults: PropTypes.number.isRequired,
        kids: PropTypes.number.isRequired,
        guest_name: PropTypes.string.isRequired,
        rsvp: PropTypes.string,
        guest_type: PropTypes.string.isRequired,
        rsvp_comment: PropTypes.string,
        rsvp_when: PropTypes.string,
        post_likes: PropTypes.number,
        liked_by: PropTypes.arrayOf(PropTypes.string),
        post_id: PropTypes.string.isRequired,
        date_viewed: PropTypes.string,
        delivery_status: PropTypes.string,
        replies: PropTypes.arrayOf(
          PropTypes.shape({
            user_id: PropTypes.string,
            guest_id: PropTypes.string.isRequired,
            guest_name: PropTypes.string.isRequired,
            comment: PropTypes.string.isRequired,
            comment_when: PropTypes.string.isRequired,
            post_likes: PropTypes.number,
            liked_by: PropTypes.arrayOf(PropTypes.string),
            post_id: PropTypes.string.isRequired,
          })
        ),
      })
    ),
  }),
  preview: PropTypes.bool,
};
